import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  KeyIcon,
  BellIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  DocumentCheckIcon,
  UsersIcon,
  DeviceTabletIcon,
  ClockIcon,
  CalendarDaysIcon,
  CalendarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { roleManagementAPI, employeePinAPI, kioskSettingsAPI, checkinSettingsAPI, schedulesAPI, leaveAPI, documentsAPI, probationaryAPI } from '../services/api';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    job_title: '',
    department: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [companySettings, setCompanySettings] = useState({
    company_name: 'Your Company Ltd',
    company_address: '123 Business Street, London, UK',
    paye_reference: 'ABC/123456789',
    company_registration: '12345678',
    vat_number: 'GB123456789',
    default_working_hours: 8,
    default_holiday_entitlement: 28,
    pay_frequency: 'monthly'
  });
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    leave_request_notifications: true,
    payroll_notifications: true,
    system_updates: true
  });
  const [approvalWorkflows, setApprovalWorkflows] = useState({
    leave_approval_required: true,
    leave_approval_levels: 1,
    leave_auto_approve_limit: 0,
    payroll_approval_required: true,
    payroll_approval_levels: 2,
    employee_changes_approval_required: true,
    manager_can_approve_leaves: true,
    hr_can_override_approvals: true,
    auto_approve_sick_leave: true,
    require_manager_approval_over_days: 5
  });
  const [availableRoles, setAvailableRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState('admin');
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [newRoleDisplayName, setNewRoleDisplayName] = useState('');
  const [newRoleCategory, setNewRoleCategory] = useState('default');
  const [copyFromRole, setCopyFromRole] = useState('');
  const [editingRole, setEditingRole] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  

  // Check-in settings state
  const [checkinSettings, setCheckinSettings] = useState({
    check_in_start_time: '09:00',
    grace_time_minutes: 15
  });

  // Leave settings state
  const [leaveSettings, setLeaveSettings] = useState({
    leave_approval_required: 'true',
    max_consecutive_leave_days: '30',
    advance_notice_days: '7',
    allow_partial_days: 'false',
    auto_approve_below_days: '0',
    carry_forward_limit: '5',
    notification_enabled: 'true',
    working_days_only: 'true',
    block_past_date_requests: 'false',
    allow_past_date_requests: 'true',
    max_past_days_allowed: '30',
    require_manager_approval: 'true'
  });

  const [kioskPinData, setKioskPinData] = useState({
    pin: '',
    confirmPin: ''
  });
  const [hasKioskPin, setHasKioskPin] = useState(false);
  
  // Kiosk management state
  const [kioskSettings, setKioskSettings] = useState({
    kiosk_username: 'kiosk',
    kiosk_password: '',
    is_active: true,
    auto_login_enabled: true,
    session_timeout_minutes: 30
  });

  // Schedule configuration state
  const [scheduleTemplates, setScheduleTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    start_time: '09:00',
    end_time: '17:00',
    break_duration_minutes: 60,
    is_working_day: true
  });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [weeklyPatterns, setWeeklyPatterns] = useState([]);
  const [newWeeklyPattern, setNewWeeklyPattern] = useState({
    name: '',
    description: '',
    monday_template_id: '',
    tuesday_template_id: '',
    wednesday_template_id: '',
    thursday_template_id: '',
    friday_template_id: '',
    saturday_template_id: '',
    sunday_template_id: ''
  });

  // Document types settings state
  const [documentTypeSettings, setDocumentTypeSettings] = useState({
    document_types_configurable: true,
    available_document_types: ['Passport', 'Driving License', 'Work Visa', 'Birth Certificate', 'National Insurance Card', 'Health Certificate', 'Professional License', 'Degree Certificate', 'Marriage Certificate', 'Biometric Residence Permit (BRP)', 'Residence Permit', 'Work Permit', 'eVisa'],
    document_field_mapping: {
      'Passport': { employee_field: 'passport_number', expiry_field: null },
      'Driving License': { employee_field: 'driving_license_number', expiry_field: 'driving_license_expiry' },
      'Work Visa': { employee_field: 'visa_type', expiry_field: 'visa_expiry_date' },
      'Visa': { employee_field: 'visa_type', expiry_field: 'visa_expiry_date' }
    }
  });
  const [newDocumentType, setNewDocumentType] = useState('');
  const [probationarySettings, setProbationarySettings] = useState({
    probationary_period_defaults: {
      duration_months: 6,
      available_probation_periods: [3, 6, 9, 12],
      review_intervals: [1, 3, 6],
      notification_days_before: [30, 14, 7, 3, 1],
      review_lead_time_days: 14
    },
    probationary_notifications: {
      enabled: true,
      notify_hr: true,
      notify_manager: true,
      email_enabled: true,
      in_app_enabled: true
    },
    probationary_reviews: {
      required_reviews: 3,
      allow_extensions: true,
      max_extensions: 2,
      extension_duration_months: 3
    }
  });

  const { user, updateUser } = useAuth();

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        job_title: user.job_title || '',
        department: user.department || ''
      });
    }
  }, [user]);

  // Load roles and permissions on component mount
  useEffect(() => {
    loadRolesAndPermissions();
  }, []);

  // Load kiosk PIN status
  useEffect(() => {
    const loadKioskPinStatus = async () => {
      if (user?.employee_id) {
        try {
          const response = await employeePinAPI.setPinStatus(user.employee_id);
          setHasKioskPin(response.data.hasPin);
        } catch (error) {
          console.error('Error loading kiosk PIN status:', error);
        }
      }
    };

    loadKioskPinStatus();
  }, [user]);

  // Load kiosk settings for admins
  useEffect(() => {
    const loadKioskSettings = async () => {
      if (user?.role === 'admin') {
        try {
          const response = await kioskSettingsAPI.getSettings();
          setKioskSettings(response.data.settings);
        } catch (error) {
          console.error('Error loading kiosk settings:', error);
        }
      }
    };

    loadKioskSettings();
  }, [user]);


  // Load data when needed
  useEffect(() => {
    if (activeTab === 'checkin-settings' && (user?.role === 'admin' || user?.role === 'hr_manager')) {
      loadCheckinSettings();
    }
    if (activeTab === 'schedule-config' && (user?.role === 'admin' || user?.role === 'hr_manager')) {
      loadScheduleTemplates();
      loadWeeklyPatterns();
    }
    if (activeTab === 'document-types' && (user?.role === 'admin' || user?.role === 'hr_manager')) {
      loadDocumentTypeSettings();
    }
    if (activeTab === 'probationary' && (user?.role === 'admin' || user?.role === 'hr_manager')) {
      loadProbationarySettings();
    }
    if (activeTab === 'leave-settings' && (user?.role === 'admin' || user?.role === 'hr_manager')) {
      loadLeaveSettings();
    }
  }, [activeTab, user]);

  const loadRolesAndPermissions = async () => {
    try {
      setLoading(true);
      const [rolesResponse, permissionsResponse] = await Promise.all([
        roleManagementAPI.getAllRoles(),
        roleManagementAPI.getAllPermissions()
      ]);
      
      const roles = rolesResponse.data?.roles || [];
      const permissions = permissionsResponse.data?.permissions || [];
      
      setAvailableRoles(roles);
      setAllPermissions(permissions);
      
      console.log('Loaded roles:', roles);
      console.log('Loaded permissions:', permissions);
      
      // Set first role as selected if not already set
      if (roles.length > 0 && !selectedRole) {
        setSelectedRole(roles[0].name);
      }
    } catch (error) {
      console.error('Error loading roles and permissions:', error);
      setMessage('Failed to load roles and permissions');
    } finally {
      setLoading(false);
    }
  };


  const tabs = [
    {
      id: 'profile',
      name: 'Profile',
      icon: UserIcon,
      description: 'Manage your personal information'
    },
    {
      id: 'security',
      name: 'Security',
      icon: KeyIcon,
      description: 'Change password and security settings'
    },
    {
      id: 'kiosk',
      name: 'Kiosk PIN',
      icon: ShieldCheckIcon,
      description: 'Setup PIN for kiosk check-in/out'
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: BellIcon,
      description: 'Configure notification preferences'
    },
    {
      id: 'company',
      name: 'Company Settings',
      icon: BuildingOfficeIcon,
      description: 'Company information and defaults'
    },
    {
      id: 'workflows',
      name: 'Approval Workflows',
      icon: DocumentCheckIcon,
      description: 'Configure approval processes and workflows'
    },
    {
      id: 'roles',
      name: 'Role Management',
      icon: UsersIcon,
      description: 'Create and manage user roles with advanced permissions'
    },
    {
      id: 'kiosk-management',
      name: 'Kiosk Management',
      icon: DeviceTabletIcon,
      description: 'Configure kiosk mode settings and authentication'
    },
    {
      id: 'checkin-settings',
      name: 'Check-in Settings',
      icon: ClockIcon,
      description: 'Configure check-in start time and grace period settings'
    },
    {
      id: 'schedule-config',
      name: 'Schedule Configuration',
      icon: CalendarDaysIcon,
      description: 'Manage schedule templates and weekly patterns'
    },
    {
      id: 'leave-settings',
      name: 'Leave Management',
      icon: CalendarIcon,
      description: 'Configure leave policies, approval workflows, and notifications'
    },
    {
      id: 'document-types',
      name: 'Document Types',
      icon: DocumentTextIcon,
      description: 'Configure available document types and field mappings'
    },
    {
      id: 'probationary',
      name: 'Probationary Periods',
      icon: AcademicCapIcon,
      description: 'Configure probationary period settings and review schedules'
    },
    {
      id: 'system',
      name: 'System',
      icon: Cog6ToothIcon,
      description: 'System configuration and maintenance'
    }
  ];

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update user context
      updateUser({ ...user, ...profileData });
      setMessage('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage('New passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordData.new_password.length < 8) {
      setMessage('New password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setMessage('Password changed successfully');
    } catch (error) {
      console.error('Error changing password:', error);
      setMessage('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySettingsUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setMessage('Company settings updated successfully');
    } catch (error) {
      console.error('Error updating company settings:', error);
      setMessage('Failed to update company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationUpdate = async () => {
    setLoading(true);
    setMessage('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setMessage('Notification settings updated successfully');
    } catch (error) {
      console.error('Error updating notifications:', error);
      setMessage('Failed to update notification settings');
    } finally {
      setLoading(false);
    }
  };


  // Leave settings functions
  const loadLeaveSettings = async () => {
    try {
      setLoading(true);
      const response = await leaveAPI.getSettings();
      const settings = response.data.settings || {};
      
      // Convert settings to the expected format
      const formattedSettings = {};
      Object.keys(settings).forEach(key => {
        formattedSettings[key] = settings[key].value || settings[key];
      });
      
      setLeaveSettings(formattedSettings);
    } catch (error) {
      console.error('Error loading leave settings:', error);
      setMessage('Failed to load leave settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveSettingsUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await leaveAPI.updateSettings({ settings: leaveSettings });
      setMessage('Leave settings updated successfully');
    } catch (error) {
      console.error('Error updating leave settings:', error);
      setMessage(error.response?.data?.message || 'Failed to update leave settings');
    } finally {
      setLoading(false);
    }
  };

  const handleKioskPinSetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (kioskPinData.pin !== kioskPinData.confirmPin) {
      setMessage('PINs do not match');
      setLoading(false);
      return;
    }

    if (kioskPinData.pin.length < 4 || kioskPinData.pin.length > 8) {
      setMessage('PIN must be 4-8 digits');
      setLoading(false);
      return;
    }

    if (!/^\d+$/.test(kioskPinData.pin)) {
      setMessage('PIN must contain only numbers');
      setLoading(false);
      return;
    }

    try {
      await employeePinAPI.setPin(user.employee_id, kioskPinData.pin);
      setHasKioskPin(true);
      setKioskPinData({ pin: '', confirmPin: '' });
      setMessage('Kiosk PIN set successfully');
    } catch (error) {
      console.error('Error setting kiosk PIN:', error);
      setMessage(error.response?.data?.message || 'Failed to set kiosk PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleKioskPinRemove = async () => {
    if (!window.confirm('Are you sure you want to remove your kiosk PIN? You will need to set up a new PIN to use kiosk check-in.')) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await employeePinAPI.removePin(user.employee_id);
      setHasKioskPin(false);
      setKioskPinData({ pin: '', confirmPin: '' });
      setMessage('Kiosk PIN removed successfully');
    } catch (error) {
      console.error('Error removing kiosk PIN:', error);
      setMessage(error.response?.data?.message || 'Failed to remove kiosk PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleKioskSettingsUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await kioskSettingsAPI.updateSettings(kioskSettings);
      setMessage('Kiosk settings updated successfully');
      
      // Clear password field after successful update
      setKioskSettings(prev => ({ ...prev, kiosk_password: '' }));
    } catch (error) {
      console.error('Error updating kiosk settings:', error);
      setMessage(error.response?.data?.message || 'Failed to update kiosk settings');
    } finally {
      setLoading(false);
    }
  };

  const loadCheckinSettings = async () => {
    try {
      const response = await checkinSettingsAPI.getSettings();
      if (response.data.success) {
        setCheckinSettings({
          check_in_start_time: response.data.settings.check_in_start_time?.substring(0, 5) || '09:00',
          grace_time_minutes: response.data.settings.grace_time_minutes || 15
        });
      }
    } catch (error) {
      console.error('Error loading check-in settings:', error);
    }
  };

  const handleCheckinSettingsUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await checkinSettingsAPI.updateSettings(checkinSettings);
      if (response.data.success) {
        setMessage('Check-in settings updated successfully');
      } else {
        setMessage(response.data.message || 'Failed to update check-in settings');
      }
    } catch (error) {
      console.error('Error updating check-in settings:', error);
      setMessage(error.response?.data?.message || 'Failed to update check-in settings');
    } finally {
      setLoading(false);
    }
  };

  // Schedule configuration functions
  const loadScheduleTemplates = async () => {
    try {
      setLoading(true);
      const response = await schedulesAPI.getTemplates();
      setScheduleTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error loading schedule templates:', error);
      setMessage('Failed to load schedule templates');
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklyPatterns = async () => {
    try {
      const response = await schedulesAPI.getWeeklyPatterns();
      setWeeklyPatterns(response.data.patterns || []);
    } catch (error) {
      console.error('Error loading weekly patterns:', error);
      setMessage('Failed to load weekly patterns');
    }
  };

  // Document Type Settings Functions
  const loadDocumentTypeSettings = async () => {
    try {
      const response = await documentsAPI.getDocumentTypeSettings();
      const settings = response.data;
      
      setDocumentTypeSettings({
        document_types_configurable: settings.document_types_configurable?.value || true,
        available_document_types: settings.available_document_types?.value || ['Passport', 'Driving License', 'Work Visa'],
        document_field_mapping: settings.document_field_mapping?.value || {}
      });
    } catch (error) {
      console.error('Error loading document type settings:', error);
    }
  };

  const saveDocumentTypeSettings = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      await documentsAPI.updateDocumentTypeSettings(documentTypeSettings);
      setMessage('Document type settings saved successfully');
    } catch (error) {
      console.error('Error saving document type settings:', error);
      setMessage('Error saving document type settings');
    } finally {
      setLoading(false);
    }
  };

  const addDocumentType = () => {
    if (newDocumentType.trim() && !documentTypeSettings.available_document_types.includes(newDocumentType.trim())) {
      setDocumentTypeSettings(prev => ({
        ...prev,
        available_document_types: [...prev.available_document_types, newDocumentType.trim()]
      }));
      setNewDocumentType('');
    }
  };

  const removeDocumentType = (typeToRemove) => {
    setDocumentTypeSettings(prev => ({
      ...prev,
      available_document_types: prev.available_document_types.filter(type => type !== typeToRemove),
      document_field_mapping: Object.fromEntries(
        Object.entries(prev.document_field_mapping).filter(([key]) => key !== typeToRemove)
      )
    }));
  };

  const updateFieldMapping = (documentType, field, value) => {
    setDocumentTypeSettings(prev => ({
      ...prev,
      document_field_mapping: {
        ...prev.document_field_mapping,
        [documentType]: {
          ...prev.document_field_mapping[documentType],
          [field]: value
        }
      }
    }));
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await schedulesAPI.createTemplate(newTemplate);
      setMessage('Schedule template created successfully');
      setNewTemplate({
        name: '',
        description: '',
        start_time: '09:00',
        end_time: '17:00',
        break_duration_minutes: 60,
        is_working_day: true
      });
      loadScheduleTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      setMessage(error.response?.data?.message || 'Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await schedulesAPI.updateTemplate(editingTemplate.id, editingTemplate);
      setMessage('Schedule template updated successfully');
      setEditingTemplate(null);
      loadScheduleTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      setMessage(error.response?.data?.message || 'Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await schedulesAPI.deleteTemplate(templateId);
      setMessage('Schedule template deleted successfully');
      loadScheduleTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      setMessage(error.response?.data?.message || 'Failed to delete template');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWeeklyPattern = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await schedulesAPI.createWeeklyPattern(newWeeklyPattern);
      setMessage('Weekly pattern created successfully');
      setNewWeeklyPattern({
        name: '',
        description: '',
        monday_template_id: '',
        tuesday_template_id: '',
        wednesday_template_id: '',
        thursday_template_id: '',
        friday_template_id: '',
        saturday_template_id: '',
        sunday_template_id: ''
      });
      loadWeeklyPatterns();
    } catch (error) {
      console.error('Error creating weekly pattern:', error);
      setMessage(error.response?.data?.message || 'Failed to create weekly pattern');
    } finally {
      setLoading(false);
    }
  };

  const renderProfileTab = () => (
    <form onSubmit={handleProfileUpdate} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            First Name
          </label>
          <input
            type="text"
            className="form-input"
            value={profileData.first_name}
            onChange={(e) => setProfileData({
              ...profileData,
              first_name: e.target.value
            })}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name
          </label>
          <input
            type="text"
            className="form-input"
            value={profileData.last_name}
            onChange={(e) => setProfileData({
              ...profileData,
              last_name: e.target.value
            })}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Address
        </label>
        <input
          type="email"
          className="form-input"
          value={profileData.email}
          onChange={(e) => setProfileData({
            ...profileData,
            email: e.target.value
          })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number
        </label>
        <input
          type="tel"
          className="form-input"
          value={profileData.phone}
          onChange={(e) => setProfileData({
            ...profileData,
            phone: e.target.value
          })}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Job Title
          </label>
          <input
            type="text"
            className="form-input"
            value={profileData.job_title}
            onChange={(e) => setProfileData({
              ...profileData,
              job_title: e.target.value
            })}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Department
          </label>
          <select
            className="form-input"
            value={profileData.department}
            onChange={(e) => setProfileData({
              ...profileData,
              department: e.target.value
            })}
          >
            <option value="">Select Department</option>
            <option value="Human Resources">Human Resources</option>
            <option value="Development">Development</option>
            <option value="Management">Management</option>
            <option value="Sales">Sales</option>
            <option value="Marketing">Marketing</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="btn-primary"
        >
          {loading ? 'Updating...' : 'Update Profile'}
        </button>
      </div>
    </form>
  );

  const renderSecurityTab = () => (
    <form onSubmit={handlePasswordChange} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Current Password
        </label>
        <input
          type="password"
          className="form-input"
          value={passwordData.current_password}
          onChange={(e) => setPasswordData({
            ...passwordData,
            current_password: e.target.value
          })}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          New Password
        </label>
        <input
          type="password"
          className="form-input"
          value={passwordData.new_password}
          onChange={(e) => setPasswordData({
            ...passwordData,
            new_password: e.target.value
          })}
          required
          minLength="8"
        />
        <p className="mt-1 text-sm text-gray-500">
          Password must be at least 8 characters long
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Confirm New Password
        </label>
        <input
          type="password"
          className="form-input"
          value={passwordData.confirm_password}
          onChange={(e) => setPasswordData({
            ...passwordData,
            confirm_password: e.target.value
          })}
          required
        />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <ShieldCheckIcon className="h-5 w-5 text-blue-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">Security Tips</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc pl-4 space-y-1">
                <li>Use a strong, unique password</li>
                <li>Include uppercase, lowercase, numbers, and special characters</li>
                <li>Don't reuse passwords from other accounts</li>
                <li>Consider using a password manager</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="btn-primary"
        >
          {loading ? 'Changing...' : 'Change Password'}
        </button>
      </div>
    </form>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-gray-900">Email Notifications</div>
            <div className="text-sm text-gray-500">Receive notifications via email</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={notificationSettings.email_notifications}
              onChange={(e) => setNotificationSettings({
                ...notificationSettings,
                email_notifications: e.target.checked
              })}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-gray-900">Leave Request Notifications</div>
            <div className="text-sm text-gray-500">Get notified about leave request updates</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={notificationSettings.leave_request_notifications}
              onChange={(e) => setNotificationSettings({
                ...notificationSettings,
                leave_request_notifications: e.target.checked
              })}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-gray-900">Payroll Notifications</div>
            <div className="text-sm text-gray-500">Receive payroll processing updates</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={notificationSettings.payroll_notifications}
              onChange={(e) => setNotificationSettings({
                ...notificationSettings,
                payroll_notifications: e.target.checked
              })}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-gray-900">System Updates</div>
            <div className="text-sm text-gray-500">Important system announcements</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={notificationSettings.system_updates}
              onChange={(e) => setNotificationSettings({
                ...notificationSettings,
                system_updates: e.target.checked
              })}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleNotificationUpdate}
          disabled={loading}
          className="btn-primary"
        >
          {loading ? 'Updating...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );

  const renderCompanyTab = () => (
    <form onSubmit={handleCompanySettingsUpdate} className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Company Information</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name
            </label>
            <input
              type="text"
              className="form-input"
              value={companySettings.company_name}
              onChange={(e) => setCompanySettings({
                ...companySettings,
                company_name: e.target.value
              })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Address
            </label>
            <textarea
              rows="3"
              className="form-input"
              value={companySettings.company_address}
              onChange={(e) => setCompanySettings({
                ...companySettings,
                company_address: e.target.value
              })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PAYE Reference
              </label>
              <input
                type="text"
                className="form-input"
                value={companySettings.paye_reference}
                onChange={(e) => setCompanySettings({
                  ...companySettings,
                  paye_reference: e.target.value
                })}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Registration
              </label>
              <input
                type="text"
                className="form-input"
                value={companySettings.company_registration}
                onChange={(e) => setCompanySettings({
                  ...companySettings,
                  company_registration: e.target.value
                })}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              VAT Number
            </label>
            <input
              type="text"
              className="form-input"
              value={companySettings.vat_number}
              onChange={(e) => setCompanySettings({
                ...companySettings,
                vat_number: e.target.value
              })}
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Default Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Working Hours
            </label>
            <input
              type="number"
              min="1"
              max="12"
              className="form-input"
              value={companySettings.default_working_hours}
              onChange={(e) => setCompanySettings({
                ...companySettings,
                default_working_hours: parseInt(e.target.value)
              })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Holiday Entitlement (Days)
            </label>
            <input
              type="number"
              min="20"
              max="50"
              className="form-input"
              value={companySettings.default_holiday_entitlement}
              onChange={(e) => setCompanySettings({
                ...companySettings,
                default_holiday_entitlement: parseInt(e.target.value)
              })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pay Frequency
            </label>
            <select
              className="form-input"
              value={companySettings.pay_frequency}
              onChange={(e) => setCompanySettings({
                ...companySettings,
                pay_frequency: e.target.value
              })}
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="annually">Annually</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="btn-primary"
        >
          {loading ? 'Updating...' : 'Update Settings'}
        </button>
      </div>
    </form>
  );


  const renderWorkflowsTab = () => (
    <form className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Leave Management Workflows</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Require approval for leave requests</label>
              <p className="text-sm text-gray-600">All leave requests must be approved by managers</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={approvalWorkflows.leave_approval_required}
              onChange={(e) => setApprovalWorkflows({
                ...approvalWorkflows,
                leave_approval_required: e.target.checked
              })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Managers can approve leaves</label>
              <p className="text-sm text-gray-600">Line managers can approve their team's leave requests</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={approvalWorkflows.manager_can_approve_leaves}
              onChange={(e) => setApprovalWorkflows({
                ...approvalWorkflows,
                manager_can_approve_leaves: e.target.checked
              })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Auto-approve sick leave</label>
              <p className="text-sm text-gray-600">Automatically approve sick leave requests</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={approvalWorkflows.auto_approve_sick_leave}
              onChange={(e) => setApprovalWorkflows({
                ...approvalWorkflows,
                auto_approve_sick_leave: e.target.checked
              })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Require approval for leave requests over (days)
            </label>
            <input
              type="number"
              min="0"
              max="30"
              className="form-input w-24"
              value={approvalWorkflows.require_manager_approval_over_days}
              onChange={(e) => setApprovalWorkflows({
                ...approvalWorkflows,
                require_manager_approval_over_days: parseInt(e.target.value)
              })}
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Payroll Workflows</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Require payroll approval</label>
              <p className="text-sm text-gray-600">Payroll runs must be approved before processing</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={approvalWorkflows.payroll_approval_required}
              onChange={(e) => setApprovalWorkflows({
                ...approvalWorkflows,
                payroll_approval_required: e.target.checked
              })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of approval levels for payroll
            </label>
            <select
              className="form-input w-32"
              value={approvalWorkflows.payroll_approval_levels}
              onChange={(e) => setApprovalWorkflows({
                ...approvalWorkflows,
                payroll_approval_levels: parseInt(e.target.value)
              })}
            >
              <option value={1}>1 Level</option>
              <option value={2}>2 Levels</option>
              <option value={3}>3 Levels</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">General Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Employee changes require approval</label>
              <p className="text-sm text-gray-600">Changes to employee details require manager approval</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={approvalWorkflows.employee_changes_approval_required}
              onChange={(e) => setApprovalWorkflows({
                ...approvalWorkflows,
                employee_changes_approval_required: e.target.checked
              })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">HR can override approvals</label>
              <p className="text-sm text-gray-600">HR administrators can override workflow approvals</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={approvalWorkflows.hr_can_override_approvals}
              onChange={(e) => setApprovalWorkflows({
                ...approvalWorkflows,
                hr_can_override_approvals: e.target.checked
              })}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => {
            setMessage('Approval workflow settings saved successfully');
            setTimeout(() => setMessage(''), 3000);
          }}
          className="btn-primary"
        >
          Save Workflow Settings
        </button>
      </div>
    </form>
  );

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim() || !newRoleDisplayName.trim()) {
      setMessage('Role name and display name are required');
      return;
    }

    try {
      setLoading(true);
      const roleData = {
        name: newRoleName.trim().toLowerCase().replace(/\s+/g, '_'),
        display_name: newRoleDisplayName.trim(),
        description: newRoleDescription.trim() || undefined,
        category: newRoleCategory
      };

      await roleManagementAPI.createRole(roleData);
      
      // If copying from another role, copy permissions
      if (copyFromRole) {
        try {
          const permissionsResponse = await roleManagementAPI.getRolePermissions(copyFromRole);
          if (permissionsResponse.data.permissions) {
            await roleManagementAPI.updateRolePermissions(roleData.name, permissionsResponse.data.permissions);
          }
        } catch (permError) {
          console.warn('Failed to copy permissions:', permError);
        }
      }
      
      setMessage('Role created successfully');
      resetCreateForm();
      
      // Reload roles
      await loadRolesAndPermissions();
      
      // Select the new role
      setSelectedRole(roleData.name);
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating role:', error);
      setMessage(error.response?.data?.message || 'Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  const resetCreateForm = () => {
    setNewRoleName('');
    setNewRoleDisplayName('');
    setNewRoleDescription('');
    setNewRoleCategory('default');
    setCopyFromRole('');
  };

  const handleEditRole = async (role) => {
    try {
      setEditingRole({
        id: role.id,
        name: role.name,
        display_name: role.display_name || role.name,
        description: role.description || '',
        category: role.category || 'default',
        is_active: role.is_active
      });
      setShowEditModal(true);
    } catch (error) {
      console.error('Error setting up role edit:', error);
      setMessage('Failed to edit role');
    }
  };

  const handleUpdateRole = async (e) => {
    e.preventDefault();
    if (!editingRole || !editingRole.display_name.trim()) {
      setMessage('Display name is required');
      return;
    }

    try {
      setLoading(true);
      const updateData = {
        display_name: editingRole.display_name.trim(),
        description: editingRole.description.trim() || undefined,
        category: editingRole.category,
        is_active: editingRole.is_active
      };

      await roleManagementAPI.updateRole(editingRole.id, updateData);
      
      setMessage('Role updated successfully');
      setShowEditModal(false);
      setEditingRole(null);
      
      // Reload roles
      await loadRolesAndPermissions();
    } catch (error) {
      console.error('Error updating role:', error);
      setMessage(error.response?.data?.message || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleName) => {
    if (!window.confirm(`Are you sure you want to delete the role "${roleName}"?`)) return;

    try {
      setLoading(true);
      await roleManagementAPI.deleteRole(roleName);
      
      setMessage('Role deleted successfully');
      
      // Reload roles
      await loadRolesAndPermissions();
      
      // Reset selected role if it was deleted
      if (selectedRole === roleName) {
        // This will be handled by the useEffect after roles are reloaded
        setSelectedRole('');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      setMessage(error.response?.data?.message || 'Failed to delete role');
    } finally {
      setLoading(false);
    }
  };


  // Role Management Tab - Master Data Management (Similar to Odoo 18)
  const renderRoleManagementTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Role Master Data Management</h3>
        <p className="text-sm text-gray-600 mb-6">
          Create and manage user roles with categories and hierarchies for advanced access control.
        </p>
      </div>

      {/* Create Role Button */}
      <div className="flex justify-between items-center">
        <div></div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn-primary flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Create New Role
        </button>
      </div>

      {/* Role Creation Section */}
      {showCreateForm && (
        <div className="border border-gray-200 rounded-lg p-6 bg-blue-50">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium text-gray-900">Create New Role</h4>
            <button
              onClick={() => {
                setShowCreateForm(false);
                resetCreateForm();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          <form onSubmit={handleCreateRole}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role Name *</label>
                <input
                  type="text"
                  className="form-input w-full"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g., team_leader, qa_tester"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Internal name (lowercase, underscores only)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Display Name *</label>
                <input
                  type="text"
                  className="form-input w-full"
                  value={newRoleDisplayName}
                  onChange={(e) => setNewRoleDisplayName(e.target.value)}
                  placeholder="e.g., Team Leader, QA Tester"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select 
                  className="form-input w-full"
                  value={newRoleCategory}
                  onChange={(e) => setNewRoleCategory(e.target.value)}
                >
                  <option value="management">Management</option>
                  <option value="employee">Employee</option>
                  <option value="administration">Administration</option>
                  <option value="finance">Finance</option>
                  <option value="human_resources">Human Resources</option>
                  <option value="default">Default</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Copy From Role</label>
                <select 
                  className="form-input w-full"
                  value={copyFromRole}
                  onChange={(e) => setCopyFromRole(e.target.value)}
                >
                  <option value="">Start from scratch</option>
                  {availableRoles.map(role => (
                    <option key={role.id} value={role.name}>{role.display_name || role.name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  className="form-input w-full"
                  rows={2}
                  value={newRoleDescription}
                  onChange={(e) => setNewRoleDescription(e.target.value)}
                  placeholder="Describe the role's responsibilities and access level"
                />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button 
                type="submit" 
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Role'}
              </button>
              <button 
                type="button"
                onClick={resetCreateForm}
                className="btn-secondary"
              >
                Reset Form
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Existing Roles Section */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Existing Roles</h4>
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {availableRoles.map((role) => (
                <tr key={role.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{role.display_name || role.name}</div>
                      <div className="text-sm text-gray-500">{role.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                      {role.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      role.is_system_role 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {role.is_system_role ? 'System' : 'Custom'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      role.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {role.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleEditRole(role)}
                        className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Edit
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedRole(role.name);
                          setActiveTab('permissions');
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Permissions
                      </button>
                      {!role.is_system_role && (
                        <button 
                          onClick={() => handleDeleteRole(role.name)}
                          className="text-red-600 hover:text-red-900 flex items-center gap-1"
                        >
                          <TrashIcon className="h-4 w-4" />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Role Modal */}
      {showEditModal && editingRole && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowEditModal(false)}></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleUpdateRole}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Edit Role</h3>
                    <p className="text-sm text-gray-500">Update role information and settings</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Role Name</label>
                      <input
                        type="text"
                        className="form-input w-full bg-gray-100"
                        value={editingRole.name}
                        disabled
                      />
                      <p className="text-xs text-gray-500 mt-1">Role name cannot be changed</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Display Name *</label>
                      <input
                        type="text"
                        className="form-input w-full"
                        value={editingRole.display_name}
                        onChange={(e) => setEditingRole({...editingRole, display_name: e.target.value})}
                        placeholder="e.g., Team Leader, QA Tester"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <select 
                        className="form-input w-full"
                        value={editingRole.category}
                        onChange={(e) => setEditingRole({...editingRole, category: e.target.value})}
                      >
                        <option value="management">Management</option>
                        <option value="employee">Employee</option>
                        <option value="administration">Administration</option>
                        <option value="finance">Finance</option>
                        <option value="human_resources">Human Resources</option>
                        <option value="default">Default</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        className="form-input w-full"
                        rows={3}
                        value={editingRole.description}
                        onChange={(e) => setEditingRole({...editingRole, description: e.target.value})}
                        placeholder="Describe the role's responsibilities and access level"
                      />
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        checked={editingRole.is_active}
                        onChange={(e) => setEditingRole({...editingRole, is_active: e.target.checked})}
                      />
                      <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                        Active
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Role'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingRole(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );


  const renderKioskTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Kiosk Check-in PIN</h3>
        <p className="text-sm text-gray-600 mb-6">
          Set up a secure PIN to use for checking in and out at kiosk terminals.
        </p>
      </div>

      {hasKioskPin ? (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <ShieldCheckIcon className="h-5 w-5 text-green-400 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-green-800">PIN Setup Complete</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Your kiosk PIN is active and ready to use for check-in/out at kiosk terminals.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => {
                setHasKioskPin(false);
                setMessage('');
              }}
              className="btn-secondary"
            >
              Change PIN
            </button>
            <button
              onClick={handleKioskPinRemove}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            >
              {loading ? 'Removing...' : 'Remove PIN'}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleKioskPinSetup} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter PIN (4-8 digits)
            </label>
            <input
              type="password"
              className="form-input w-32"
              value={kioskPinData.pin}
              onChange={(e) => setKioskPinData({
                ...kioskPinData,
                pin: e.target.value.replace(/\D/g, '').slice(0, 8)
              })}
              placeholder="1234"
              required
              minLength="4"
              maxLength="8"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm PIN
            </label>
            <input
              type="password"
              className="form-input w-32"
              value={kioskPinData.confirmPin}
              onChange={(e) => setKioskPinData({
                ...kioskPinData,
                confirmPin: e.target.value.replace(/\D/g, '').slice(0, 8)
              })}
              placeholder="1234"
              required
              minLength="4"
              maxLength="8"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <ShieldCheckIcon className="h-5 w-5 text-blue-400 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-blue-800">PIN Security Guidelines</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc pl-4 space-y-1">
                    <li>PIN must be 4-8 digits long</li>
                    <li>Use a PIN that's easy for you to remember but hard for others to guess</li>
                    <li>Don't use obvious combinations like 1234 or your birth year</li>
                    <li>Keep your PIN confidential and don't share it with others</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Setting up...' : 'Setup Kiosk PIN'}
            </button>
          </div>
        </form>
      )}
    </div>
  );

  const renderKioskManagementTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Kiosk System Configuration</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure kiosk mode settings for employee self-service check-in/out terminals.
        </p>
      </div>

      <form onSubmit={handleKioskSettingsUpdate} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kiosk Username
            </label>
            <input
              type="text"
              className="form-input"
              value={kioskSettings.kiosk_username}
              onChange={(e) => setKioskSettings(prev => ({
                ...prev,
                kiosk_username: e.target.value
              }))}
              required
              minLength="3"
              maxLength="50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Username for kiosk mode authentication
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kiosk Password
            </label>
            <input
              type="password"
              className="form-input"
              value={kioskSettings.kiosk_password}
              onChange={(e) => setKioskSettings(prev => ({
                ...prev,
                kiosk_password: e.target.value
              }))}
              placeholder="Leave blank to keep current password"
              minLength="6"
            />
            <p className="text-xs text-gray-500 mt-1">
              Password for kiosk mode access (leave blank to keep current)
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                className="form-checkbox h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                checked={kioskSettings.is_active}
                onChange={(e) => setKioskSettings(prev => ({
                  ...prev,
                  is_active: e.target.checked
                }))}
              />
              <span className="ml-2 text-sm text-gray-700">Enable Kiosk Mode</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Allow employees to access kiosk terminals
            </p>
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                className="form-checkbox h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                checked={kioskSettings.auto_login_enabled}
                onChange={(e) => setKioskSettings(prev => ({
                  ...prev,
                  auto_login_enabled: e.target.checked
                }))}
              />
              <span className="ml-2 text-sm text-gray-700">Auto-login for Kiosk</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Automatically authenticate kiosk terminals
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Session Timeout (minutes)
          </label>
          <input
            type="number"
            className="form-input w-32"
            value={kioskSettings.session_timeout_minutes}
            onChange={(e) => setKioskSettings(prev => ({
              ...prev,
              session_timeout_minutes: parseInt(e.target.value) || 30
            }))}
            min="5"
            max="120"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Automatic timeout for inactive kiosk sessions
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <DeviceTabletIcon className="h-5 w-5 text-blue-400 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">Kiosk Access Information</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p><strong>Kiosk URL:</strong> {window.location.origin}/kiosk</p>
                <p className="mt-1"><strong>Current Username:</strong> {kioskSettings.kiosk_username}</p>
                <p className="mt-2">
                  Use these credentials to set up kiosk terminals. The kiosk mode provides a dedicated 
                  interface for employee self-service check-in/out without requiring individual logins.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => window.open('/kiosk', '_blank')}
            className="btn-secondary"
          >
            <DeviceTabletIcon className="w-4 h-4 mr-2" />
            Test Kiosk Mode
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Saving...' : 'Save Kiosk Settings'}
          </button>
        </div>
      </form>
    </div>
  );

  const renderSystemTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">System Information</h3>
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-700">System Version:</span>
            <span className="text-sm text-gray-900">v2.1.0</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-700">Last Update:</span>
            <span className="text-sm text-gray-900">2025-01-15</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-700">Database Status:</span>
            <span className="text-sm text-green-600">Connected</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-700">HMRC Integration:</span>
            <span className="text-sm text-green-600">Active</span>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Maintenance</h3>
        <div className="space-y-4">
          <button className="btn-secondary w-full justify-start">
            <DocumentTextIcon className="w-5 h-5 mr-3" />
            Export System Logs
          </button>
          <button className="btn-secondary w-full justify-start">
            <ShieldCheckIcon className="w-5 h-5 mr-3" />
            Run Security Audit
          </button>
          <button className="btn-secondary w-full justify-start">
            <Cog6ToothIcon className="w-5 h-5 mr-3" />
            Clear System Cache
          </button>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ShieldCheckIcon className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Backup Recommendation
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                It's recommended to backup your system regularly. Last backup was performed 7 days ago.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCheckinSettingsTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Check-in Time Configuration</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure the official check-in start time and grace period for attendance tracking.
        </p>
        
        <form onSubmit={handleCheckinSettingsUpdate} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Check-in Start Time *
              </label>
              <input
                type="time"
                required
                className="form-input"
                value={checkinSettings.check_in_start_time}
                onChange={(e) => setCheckinSettings({
                  ...checkinSettings,
                  check_in_start_time: e.target.value
                })}
              />
              <p className="mt-1 text-xs text-gray-500">
                Official start time for work day (e.g., 09:00)
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grace Time (Minutes) *
              </label>
              <input
                type="number"
                required
                min="0"
                max="60"
                className="form-input"
                value={checkinSettings.grace_time_minutes}
                onChange={(e) => setCheckinSettings({
                  ...checkinSettings,
                  grace_time_minutes: parseInt(e.target.value) || 0
                })}
                placeholder="15"
              />
              <p className="mt-1 text-xs text-gray-500">
                Grace period in minutes after start time (0-60 minutes)
              </p>
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Early Check-in Detection</h4>
            <p className="text-sm text-blue-700">
              Employees who check in before <strong>{checkinSettings.check_in_start_time}</strong> will be flagged as "early check-ins" 
              in the Early Check-in Report. Use this to monitor and manage early arrivals.
            </p>
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderScheduleConfigTab = () => (
    <div className="space-y-6">
      {/* Schedule Templates Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Schedule Templates</h3>
        <p className="text-sm text-gray-600 mb-6">
          Create reusable schedule templates for different shift patterns.
        </p>
        
        {/* Create New Template Form */}
        <form onSubmit={handleCreateTemplate} className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-md font-medium text-gray-900 mb-4">Add New Template</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Name *
              </label>
              <input
                type="text"
                required
                className="form-input"
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                placeholder="e.g., Standard Day Shift"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time
              </label>
              <input
                type="time"
                className="form-input"
                value={newTemplate.start_time}
                onChange={(e) => setNewTemplate({...newTemplate, start_time: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time
              </label>
              <input
                type="time"
                className="form-input"
                value={newTemplate.end_time}
                onChange={(e) => setNewTemplate({...newTemplate, end_time: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Break Duration (minutes)
              </label>
              <input
                type="number"
                min="0"
                max="480"
                className="form-input"
                value={newTemplate.break_duration_minutes}
                onChange={(e) => setNewTemplate({...newTemplate, break_duration_minutes: parseInt(e.target.value) || 0})}
                placeholder="60"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 text-primary-600"
                  checked={newTemplate.is_working_day}
                  onChange={(e) => setNewTemplate({...newTemplate, is_working_day: e.target.checked})}
                />
                <span className="ml-2 text-sm text-gray-700">Working Day</span>
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                className="form-input"
                value={newTemplate.description}
                onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                placeholder="Brief description of this template"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="submit" disabled={loading} className="btn-primary">
              <PlusIcon className="w-4 h-4 mr-2" />
              {loading ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>

        {/* Templates List */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Break</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {scheduleTemplates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingTemplate?.id === template.id ? (
                      <input
                        type="text"
                        className="form-input text-sm"
                        value={editingTemplate.name}
                        onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                      />
                    ) : (
                      <div className="text-sm font-medium text-gray-900">{template.name}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingTemplate?.id === template.id ? (
                      <div className="flex space-x-2">
                        <input
                          type="time"
                          className="form-input text-sm w-24"
                          value={editingTemplate.start_time || ''}
                          onChange={(e) => setEditingTemplate({...editingTemplate, start_time: e.target.value})}
                        />
                        <span className="text-gray-500">-</span>
                        <input
                          type="time"
                          className="form-input text-sm w-24"
                          value={editingTemplate.end_time || ''}
                          onChange={(e) => setEditingTemplate({...editingTemplate, end_time: e.target.value})}
                        />
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">
                        {template.start_time && template.end_time ? 
                          `${template.start_time} - ${template.end_time}` : 
                          'No schedule'
                        }
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingTemplate?.id === template.id ? (
                      <input
                        type="number"
                        className="form-input text-sm w-20"
                        value={editingTemplate.break_duration_minutes}
                        onChange={(e) => setEditingTemplate({...editingTemplate, break_duration_minutes: parseInt(e.target.value) || 0})}
                      />
                    ) : (
                      <div className="text-sm text-gray-500">{template.break_duration_minutes || 0} min</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingTemplate?.id === template.id ? (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-primary-600"
                          checked={editingTemplate.is_working_day}
                          onChange={(e) => setEditingTemplate({...editingTemplate, is_working_day: e.target.checked})}
                        />
                        <span className="ml-2 text-sm">Working</span>
                      </label>
                    ) : (
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        template.is_working_day 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {template.is_working_day ? 'Working Day' : 'Non-Working'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingTemplate?.id === template.id ? (
                      <input
                        type="text"
                        className="form-input text-sm"
                        value={editingTemplate.description || ''}
                        onChange={(e) => setEditingTemplate({...editingTemplate, description: e.target.value})}
                      />
                    ) : (
                      <div className="text-sm text-gray-500">{template.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {editingTemplate?.id === template.id ? (
                      <div className="space-x-2">
                        <button
                          onClick={handleUpdateTemplate}
                          disabled={loading}
                          className="text-green-600 hover:text-green-900"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingTemplate(null)}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="space-x-2">
                        <button
                          onClick={() => setEditingTemplate(template)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          disabled={loading}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {scheduleTemplates.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    No schedule templates found. Create your first template above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Weekly Patterns Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Weekly Schedule Patterns</h3>
        <p className="text-sm text-gray-600 mb-6">
          Create weekly patterns by assigning templates to each day of the week.
        </p>
        
        {/* Create New Weekly Pattern Form */}
        <form onSubmit={handleCreateWeeklyPattern} className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-md font-medium text-gray-900 mb-4">Add New Weekly Pattern</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pattern Name *
              </label>
              <input
                type="text"
                required
                className="form-input"
                value={newWeeklyPattern.name}
                onChange={(e) => setNewWeeklyPattern({...newWeeklyPattern, name: e.target.value})}
                placeholder="e.g., Standard Week"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                className="form-input"
                value={newWeeklyPattern.description}
                onChange={(e) => setNewWeeklyPattern({...newWeeklyPattern, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
              <div key={day}>
                <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                  {day}
                </label>
                <select
                  className="form-input"
                  value={newWeeklyPattern[`${day}_template_id`]}
                  onChange={(e) => setNewWeeklyPattern({...newWeeklyPattern, [`${day}_template_id`]: e.target.value})}
                >
                  <option value="">No Schedule</option>
                  {scheduleTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          
          <div className="mt-4 flex justify-end">
            <button type="submit" disabled={loading} className="btn-primary">
              <PlusIcon className="w-4 h-4 mr-2" />
              {loading ? 'Creating...' : 'Create Weekly Pattern'}
            </button>
          </div>
        </form>

        {/* Weekly Patterns List */}
        <div className="space-y-4">
          {weeklyPatterns.map((pattern) => (
            <div key={pattern.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h4 className="text-md font-medium text-gray-900">{pattern.name}</h4>
                  <p className="text-sm text-gray-500">{pattern.description}</p>
                </div>
                <div className="space-x-2">
                  <button className="text-blue-600 hover:text-blue-900">
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button className="text-red-600 hover:text-red-900">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-2 text-xs">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
                  const dayKey = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][index];
                  const templateId = pattern[`${dayKey}_template_id`];
                  const template = scheduleTemplates.find(t => t.id === templateId);
                  
                  return (
                    <div key={day} className="text-center p-2 bg-gray-50 rounded">
                      <div className="font-medium text-gray-700">{day}</div>
                      <div className="text-gray-500 mt-1">
                        {template ? template.name : 'Off'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {weeklyPatterns.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              No weekly patterns found. Create your first pattern above.
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderLeaveSettingsTab = () => (
    <form onSubmit={handleLeaveSettingsUpdate} className="space-y-6">
      {/* Leave Policy Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Leave Policy Settings</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure how leave requests are processed and validated in your organization.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum Consecutive Leave Days
            </label>
            <input
              type="number"
              min="1"
              max="365"
              className="form-input"
              value={leaveSettings.max_consecutive_leave_days}
              onChange={(e) => setLeaveSettings({
                ...leaveSettings,
                max_consecutive_leave_days: e.target.value
              })}
            />
            <p className="text-xs text-gray-500 mt-1">Maximum days an employee can take consecutively</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Advance Notice Days
            </label>
            <input
              type="number"
              min="0"
              max="90"
              className="form-input"
              value={leaveSettings.advance_notice_days}
              onChange={(e) => setLeaveSettings({
                ...leaveSettings,
                advance_notice_days: e.target.value
              })}
            />
            <p className="text-xs text-gray-500 mt-1">Minimum days advance notice required</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Auto-approve Below Days
            </label>
            <input
              type="number"
              min="0"
              max="30"
              className="form-input"
              value={leaveSettings.auto_approve_below_days}
              onChange={(e) => setLeaveSettings({
                ...leaveSettings,
                auto_approve_below_days: e.target.value
              })}
            />
            <p className="text-xs text-gray-500 mt-1">Auto-approve requests below this number of days (0 = disabled)</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Carry Forward Limit
            </label>
            <input
              type="number"
              min="0"
              max="50"
              className="form-input"
              value={leaveSettings.carry_forward_limit}
              onChange={(e) => setLeaveSettings({
                ...leaveSettings,
                carry_forward_limit: e.target.value
              })}
            />
            <p className="text-xs text-gray-500 mt-1">Maximum days that can be carried forward to next year</p>
          </div>
        </div>
      </div>

      {/* Approval Workflow Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Approval Workflow</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure how leave requests are approved and who can approve them.
        </p>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Require approval for all leave requests</label>
              <p className="text-sm text-gray-600">All leave requests must be approved before being granted</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={leaveSettings.leave_approval_required === 'true'}
              onChange={(e) => setLeaveSettings({
                ...leaveSettings,
                leave_approval_required: e.target.checked ? 'true' : 'false'
              })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Require manager approval</label>
              <p className="text-sm text-gray-600">Employees must get approval from their direct manager</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={leaveSettings.require_manager_approval === 'true'}
              onChange={(e) => setLeaveSettings({
                ...leaveSettings,
                require_manager_approval: e.target.checked ? 'true' : 'false'
              })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Block all past date requests</label>
              <p className="text-sm text-gray-600">Completely prevent employees from requesting leave for past dates (overrides other settings)</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={leaveSettings.block_past_date_requests === 'true'}
              onChange={(e) => setLeaveSettings({
                ...leaveSettings,
                block_past_date_requests: e.target.checked ? 'true' : 'false'
              })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Allow past date requests</label>
              <p className="text-sm text-gray-600">Allow employees to request leave for past dates (subject to limits below)</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={leaveSettings.allow_past_date_requests === 'true'}
              onChange={(e) => setLeaveSettings({
                ...leaveSettings,
                allow_past_date_requests: e.target.checked ? 'true' : 'false'
              })}
              disabled={leaveSettings.block_past_date_requests === 'true'}
            />
          </div>

          {leaveSettings.allow_past_date_requests === 'true' && leaveSettings.block_past_date_requests !== 'true' && (
            <div className="ml-6 p-4 bg-gray-50 rounded-md">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Past Days Allowed
              </label>
              <input
                type="number"
                min="1"
                max="365"
                className="form-input w-32"
                value={leaveSettings.max_past_days_allowed || '30'}
                onChange={(e) => setLeaveSettings({
                  ...leaveSettings,
                  max_past_days_allowed: e.target.value
                })}
              />
              <p className="text-xs text-gray-500 mt-1">Maximum number of days in the past employees can request leave for</p>
            </div>
          )}
        </div>
      </div>

      {/* Leave Calculation Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Leave Calculation</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure how leave days are calculated and managed.
        </p>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Count working days only</label>
              <p className="text-sm text-gray-600">Only count weekdays when calculating leave duration</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={leaveSettings.working_days_only === 'true'}
              onChange={(e) => setLeaveSettings({
                ...leaveSettings,
                working_days_only: e.target.checked ? 'true' : 'false'
              })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Allow partial day leave</label>
              <p className="text-sm text-gray-600">Allow employees to request half-day or hourly leave</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={leaveSettings.allow_partial_days === 'true'}
              onChange={(e) => setLeaveSettings({
                ...leaveSettings,
                allow_partial_days: e.target.checked ? 'true' : 'false'
              })}
            />
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Settings</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure email notifications for leave requests and approvals.
        </p>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Enable email notifications</label>
              <p className="text-sm text-gray-600">Send email notifications for leave request events</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={leaveSettings.notification_enabled === 'true'}
              onChange={(e) => setLeaveSettings({
                ...leaveSettings,
                notification_enabled: e.target.checked ? 'true' : 'false'
              })}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="btn-primary"
        >
          {loading ? 'Saving...' : 'Save Leave Settings'}
        </button>
      </div>
    </form>
  );

  const renderDocumentTypesTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Document Type Configuration</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure available document types and their field mappings for automatic personal information updates.
        </p>
        
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Enable configurable document types</label>
              <p className="text-sm text-gray-600">Allow custom document types alongside system defaults</p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={documentTypeSettings.document_types_configurable}
              onChange={(e) => setDocumentTypeSettings(prev => ({
                ...prev,
                document_types_configurable: e.target.checked
              }))}
            />
          </div>
        </div>

        {documentTypeSettings.document_types_configurable && (
          <>
            <div className="mb-6">
              <h4 className="text-base font-medium text-gray-900 mb-3">Available Document Types</h4>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newDocumentType}
                  onChange={(e) => setNewDocumentType(e.target.value)}
                  placeholder="Enter document type name"
                  className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && addDocumentType()}
                />
                <button
                  onClick={addDocumentType}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {documentTypeSettings.available_document_types.map((docType, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                    <span className="text-sm font-medium text-gray-900">{docType}</span>
                    <button
                      onClick={() => removeDocumentType(docType)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-base font-medium text-gray-900 mb-3">Field Mappings</h4>
              <p className="text-sm text-gray-600 mb-4">
                Configure how document uploads automatically update employee personal information.
              </p>
              
              <div className="space-y-4">
                {documentTypeSettings.available_document_types.filter(type => 
                  ['Passport', 'Driving License', 'Work Visa', 'Visa'].includes(type)
                ).map((docType) => (
                  <div key={docType} className="border border-gray-200 rounded-md p-4">
                    <h5 className="font-medium text-gray-900 mb-3">{docType}</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Employee Field
                        </label>
                        <select
                          value={documentTypeSettings.document_field_mapping[docType]?.employee_field || ''}
                          onChange={(e) => updateFieldMapping(docType, 'employee_field', e.target.value)}
                          className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select field</option>
                          <option value="passport_number">Passport Number</option>
                          <option value="driving_license_number">Driving License Number</option>
                          <option value="visa_type">Visa Type</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Expiry Field (Optional)
                        </label>
                        <select
                          value={documentTypeSettings.document_field_mapping[docType]?.expiry_field || ''}
                          onChange={(e) => updateFieldMapping(docType, 'expiry_field', e.target.value || null)}
                          className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">No expiry field</option>
                          <option value="passport_expiry">Passport Expiry</option>
                          <option value="driving_license_expiry">Driving License Expiry</option>
                          <option value="visa_expiry_date">Visa Expiry Date</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end">
          <button
            onClick={saveDocumentTypeSettings}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );

  // Probationary Settings Functions
  const loadProbationarySettings = async () => {
    try {
      const response = await probationaryAPI.getSettings();
      console.log('API Response:', response.data); // Debug log
      const settings = response.data.data || response.data;
      
      // Ensure we have proper default structure if API returns incomplete data
      const safeSettings = {
        probationary_period_defaults: {
          duration_months: settings?.probationary_period_defaults?.duration_months || 6,
          available_probation_periods: settings?.probationary_period_defaults?.available_probation_periods || [3, 6, 9, 12],
          review_intervals: settings?.probationary_period_defaults?.review_intervals || [1, 3, 6],
          notification_days_before: settings?.probationary_period_defaults?.notification_days_before || [30, 14, 7, 3, 1],
          review_lead_time_days: settings?.probationary_period_defaults?.review_lead_time_days || 14
        },
        probationary_notifications: {
          enabled: settings?.probationary_notifications?.enabled !== undefined ? settings.probationary_notifications.enabled : true,
          notify_hr: settings?.probationary_notifications?.notify_hr !== undefined ? settings.probationary_notifications.notify_hr : true,
          notify_manager: settings?.probationary_notifications?.notify_manager !== undefined ? settings.probationary_notifications.notify_manager : true,
          email_enabled: settings?.probationary_notifications?.email_enabled !== undefined ? settings.probationary_notifications.email_enabled : true,
          in_app_enabled: settings?.probationary_notifications?.in_app_enabled !== undefined ? settings.probationary_notifications.in_app_enabled : true
        },
        probationary_reviews: {
          required_reviews: settings?.probationary_reviews?.required_reviews || 3,
          allow_extensions: settings?.probationary_reviews?.allow_extensions !== undefined ? settings.probationary_reviews.allow_extensions : true,
          max_extensions: settings?.probationary_reviews?.max_extensions || 2,
          extension_duration_months: settings?.probationary_reviews?.extension_duration_months || 3
        }
      };
      
      console.log('Loading probationary settings:', safeSettings); // Debug log
      setProbationarySettings(safeSettings);
    } catch (error) {
      console.error('Error loading probationary settings:', error);
      // Keep the default settings if API call fails
      console.log('Using default probationary settings due to API error');
    }
  };

  const saveProbationarySettings = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      await probationaryAPI.updateSettings(probationarySettings);
      setMessage('Probationary settings saved successfully');
    } catch (error) {
      console.error('Error saving probationary settings:', error);
      setMessage('Error saving probationary settings');
    } finally {
      setLoading(false);
    }
  };

  const updateProbationaryDefaults = (field, value) => {
    setProbationarySettings(prev => ({
      ...prev,
      probationary_period_defaults: {
        ...prev.probationary_period_defaults,
        [field]: value
      }
    }));
  };

  const updateProbationaryNotifications = (field, value) => {
    setProbationarySettings(prev => ({
      ...prev,
      probationary_notifications: {
        ...prev.probationary_notifications,
        [field]: value
      }
    }));
  };

  const updateProbationaryReviews = (field, value) => {
    setProbationarySettings(prev => ({
      ...prev,
      probationary_reviews: {
        ...prev.probationary_reviews,
        [field]: value
      }
    }));
  };

  const addReviewInterval = () => {
    const newInterval = parseInt(prompt('Enter review interval (in months):'));
    if (newInterval && newInterval > 0) {
      setProbationarySettings(prev => ({
        ...prev,
        probationary_period_defaults: {
          ...prev.probationary_period_defaults,
          review_intervals: [...prev.probationary_period_defaults.review_intervals, newInterval].sort((a, b) => a - b)
        }
      }));
    }
  };

  const removeReviewInterval = (intervalToRemove) => {
    setProbationarySettings(prev => ({
      ...prev,
      probationary_period_defaults: {
        ...prev.probationary_period_defaults,
        review_intervals: prev.probationary_period_defaults.review_intervals.filter(interval => interval !== intervalToRemove)
      }
    }));
  };

  const addNotificationDay = () => {
    const newDay = parseInt(prompt('Enter notification days before review:'));
    if (newDay && newDay > 0) {
      setProbationarySettings(prev => ({
        ...prev,
        probationary_period_defaults: {
          ...prev.probationary_period_defaults,
          notification_days_before: [...prev.probationary_period_defaults.notification_days_before, newDay].sort((a, b) => b - a)
        }
      }));
    }
  };

  const removeNotificationDay = (dayToRemove) => {
    setProbationarySettings(prev => ({
      ...prev,
      probationary_period_defaults: {
        ...prev.probationary_period_defaults,
        notification_days_before: prev.probationary_period_defaults.notification_days_before.filter(day => day !== dayToRemove)
      }
    }));
  };

  const renderProbationaryTab = () => (
    <div className="space-y-6">
      {/* Default Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Default Probationary Period Settings</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="duration_months" className="block text-sm font-medium text-gray-700">
              Default Probation Duration (months)
            </label>
            <div className="mt-1 flex items-center">
              <button
                type="button"
                onClick={() => {
                  const currentValue = probationarySettings?.probationary_period_defaults?.duration_months || 6;
                  if (currentValue > 1) {
                    setProbationarySettings(prev => ({
                      ...prev,
                      probationary_period_defaults: {
                        ...(prev?.probationary_period_defaults || {}),
                        duration_months: currentValue - 1
                      }
                    }));
                  }
                }}
                disabled={!probationarySettings?.probationary_period_defaults?.duration_months || probationarySettings.probationary_period_defaults.duration_months <= 1}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-l-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                −
              </button>
              <input
                type="number"
                id="duration_months"
                min="1"
                max="24"
                value={probationarySettings?.probationary_period_defaults?.duration_months || 6}
                onChange={(e) => setProbationarySettings(prev => ({
                  ...prev,
                  probationary_period_defaults: {
                    ...(prev?.probationary_period_defaults || {}),
                    duration_months: parseInt(e.target.value) || 1
                  }
                }))}
                className="block w-full border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-center"
              />
              <button
                type="button"
                onClick={() => {
                  const currentValue = probationarySettings?.probationary_period_defaults?.duration_months || 6;
                  if (currentValue < 24) {
                    setProbationarySettings(prev => ({
                      ...prev,
                      probationary_period_defaults: {
                        ...(prev?.probationary_period_defaults || {}),
                        duration_months: currentValue + 1
                      }
                    }));
                  }
                }}
                disabled={!probationarySettings?.probationary_period_defaults?.duration_months || probationarySettings.probationary_period_defaults.duration_months >= 24}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              This sets the default probation period for new employees. Existing employees will keep their current probation duration unless manually updated.
            </p>
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-700">
                <strong>📋 How it works:</strong>
              </p>
              <ul className="mt-1 text-sm text-blue-600 list-disc list-inside">
                <li>New employees automatically get this duration</li>
                <li>Probation end dates are calculated automatically</li>
                <li>Existing employees keep their current settings</li>
                <li>HR/Admin can override individual employee probation periods</li>
              </ul>
            </div>
            
            {/* Sync button for existing employees */}
            <div className="mt-4">
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm('Apply default probation duration to active employees still in probation period?\n\nThis will only update:\n• Employees without probation periods set\n• Active employees still in probation (no confirmation date)\n• Will NOT affect confirmed/permanent employees\n\nProceed?')) {
                    return;
                  }
                  
                  try {
                    setLoading(true);
                    setMessage('Syncing probation durations...');
                    
                    // Save current settings first to ensure sync happens on backend
                    await probationaryAPI.updateSettings(probationarySettings);
                    
                    setMessage('Default probation duration synced with employees successfully');
                  } catch (error) {
                    console.error('Error syncing probation durations:', error);
                    setMessage('Error syncing probation durations');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sync with Existing Employees
              </button>
              <p className="mt-1 text-xs text-gray-500">
                Apply default duration only to active employees still in probation (excludes confirmed/permanent employees)
              </p>
            </div>
          </div>
          
          {/* Available Probation Periods */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Available Probation Periods (months)
            </label>
            <div className="flex flex-wrap gap-2">
              {(probationarySettings?.probationary_period_defaults?.available_probation_periods || []).map((period, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                >
                  {period} month{period !== 1 ? 's' : ''}
                  <button
                    type="button"
                    onClick={() => setProbationarySettings(prev => ({
                      ...prev,
                      probationary_period_defaults: {
                        ...prev.probationary_period_defaults,
                        available_probation_periods: prev.probationary_period_defaults.available_probation_periods.filter(p => p !== period)
                      }
                    }))}
                    className="ml-1 text-green-600 hover:text-green-800"
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="number"
                placeholder="Add period"
                min="1"
                max="36"
                className="w-24 rounded-md border-gray-300 text-xs"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value) {
                    const newPeriod = parseInt(e.target.value);
                    if (!(probationarySettings?.probationary_period_defaults?.available_probation_periods || []).includes(newPeriod)) {
                      setProbationarySettings(prev => ({
                        ...prev,
                        probationary_period_defaults: {
                          ...prev.probationary_period_defaults,
                          available_probation_periods: [...prev.probationary_period_defaults.available_probation_periods, newPeriod].sort((a, b) => a - b)
                        }
                      }));
                    }
                    e.target.value = '';
                  }
                }}
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              These are the probation period options available in the employee create/edit forms
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Review Intervals (months)
            </label>
            <div className="flex flex-wrap gap-2">
              {(probationarySettings?.probationary_period_defaults?.review_intervals || []).map((interval, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {interval} month{interval !== 1 ? 's' : ''}
                  <button
                    type="button"
                    onClick={() => setProbationarySettings(prev => ({
                      ...prev,
                      probationary_period_defaults: {
                        ...prev.probationary_period_defaults,
                        review_intervals: prev.probationary_period_defaults.review_intervals.filter(i => i !== interval)
                      }
                    }))}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="number"
                placeholder="Add interval"
                min="1"
                max="24"
                className="w-24 rounded-md border-gray-300 text-xs"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value) {
                    const newInterval = parseInt(e.target.value);
                    if (!(probationarySettings?.probationary_period_defaults?.review_intervals || []).includes(newInterval)) {
                      setProbationarySettings(prev => ({
                        ...prev,
                        probationary_period_defaults: {
                          ...prev.probationary_period_defaults,
                          review_intervals: [...prev.probationary_period_defaults.review_intervals, newInterval].sort((a, b) => a - b)
                        }
                      }));
                    }
                    e.target.value = '';
                  }
                }}
              />
            </div>
          </div>
          
          {/* Review Lead Time */}
          <div>
            <label htmlFor="review_lead_time_days" className="block text-sm font-medium text-gray-700">
              Review Lead Time (days)
            </label>
            <div className="mt-1 flex items-center">
              <button
                type="button"
                onClick={() => {
                  const currentValue = probationarySettings?.probationary_period_defaults?.review_lead_time_days || 14;
                  if (currentValue > 1) {
                    setProbationarySettings(prev => ({
                      ...prev,
                      probationary_period_defaults: {
                        ...prev.probationary_period_defaults,
                        review_lead_time_days: currentValue - 1
                      }
                    }));
                  }
                }}
                disabled={!probationarySettings?.probationary_period_defaults?.review_lead_time_days || probationarySettings.probationary_period_defaults.review_lead_time_days <= 1}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-l-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                −
              </button>
              <input
                type="number"
                id="review_lead_time_days"
                min="1"
                max="90"
                value={probationarySettings?.probationary_period_defaults?.review_lead_time_days || 14}
                onChange={(e) => setProbationarySettings(prev => ({
                  ...prev,
                  probationary_period_defaults: {
                    ...prev.probationary_period_defaults,
                    review_lead_time_days: parseInt(e.target.value) || 1
                  }
                }))}
                className="block w-full border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-center"
              />
              <button
                type="button"
                onClick={() => {
                  const currentValue = probationarySettings?.probationary_period_defaults?.review_lead_time_days || 14;
                  if (currentValue < 90) {
                    setProbationarySettings(prev => ({
                      ...prev,
                      probationary_period_defaults: {
                        ...prev.probationary_period_defaults,
                        review_lead_time_days: currentValue + 1
                      }
                    }));
                  }
                }}
                disabled={!probationarySettings?.probationary_period_defaults?.review_lead_time_days || probationarySettings.probationary_period_defaults.review_lead_time_days >= 90}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Final reviews will be scheduled this many days before the probation end date. This ensures sufficient time to make employment decisions before probation expires.
            </p>
            <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-700">
                <strong>⚠️ Important:</strong> Changing this setting only affects new probationary periods. Use the "Fix Existing Reviews" button below to update current reviews.
              </p>
            </div>
          </div>
        </div>
        
        {/* Fix Existing Reviews Button */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm(`Apply the ${probationarySettings?.probationary_period_defaults?.review_lead_time_days || 14}-day lead time to existing pending final reviews?\n\nThis will adjust review dates that are currently scheduled too close to or after probation end dates.\n\nProceed?`)) {
                return;
              }
              
              try {
                setLoading(true);
                setMessage('Fixing existing review dates...');
                
                // First save current settings
                await probationaryAPI.updateSettings(probationarySettings);
                
                // Then call the fix method via API (you may need to add this endpoint)
                const response = await fetch('/api/probationary/fix-review-dates', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                });
                
                if (response.ok) {
                  const result = await response.json();
                  setMessage(`Successfully adjusted ${result.reviewsFixed || 0} review dates with ${result.leadTimeDays || 14}-day lead time`);
                } else {
                  throw new Error('Failed to fix review dates');
                }
              } catch (error) {
                console.error('Error fixing review dates:', error);
                setMessage('Error fixing review dates. Settings have been saved but existing reviews may need manual adjustment.');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Fix Existing Review Dates
          </button>
          <p className="mt-1 text-xs text-gray-500">
            Adjust existing pending final reviews to respect the new lead time configuration
          </p>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Enable Notifications</label>
              <p className="text-sm text-gray-500">Send notifications for upcoming reviews</p>
            </div>
            <button
              type="button"
              onClick={() => setProbationarySettings(prev => ({
                ...prev,
                probationary_notifications: {
                  ...prev.probationary_notifications,
                  enabled: !prev.probationary_notifications.enabled
                }
              }))}
              className={`${
                probationarySettings?.probationary_notifications?.enabled ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  probationarySettings?.probationary_notifications?.enabled ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>

          {probationarySettings?.probationary_notifications?.enabled && (
            <>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Notify HR Team</label>
                <button
                  type="button"
                  onClick={() => setProbationarySettings(prev => ({
                    ...prev,
                    probationary_notifications: {
                      ...prev.probationary_notifications,
                      notify_hr: !prev.probationary_notifications.notify_hr
                    }
                  }))}
                  className={`${
                    probationarySettings?.probationary_notifications?.notify_hr ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      probationarySettings?.probationary_notifications?.notify_hr ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Notify Managers</label>
                <button
                  type="button"
                  onClick={() => setProbationarySettings(prev => ({
                    ...prev,
                    probationary_notifications: {
                      ...prev.probationary_notifications,
                      notify_manager: !prev.probationary_notifications.notify_manager
                    }
                  }))}
                  className={`${
                    probationarySettings?.probationary_notifications?.notify_manager ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      probationarySettings?.probationary_notifications?.notify_manager ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                <button
                  type="button"
                  onClick={() => setProbationarySettings(prev => ({
                    ...prev,
                    probationary_notifications: {
                      ...prev.probationary_notifications,
                      email_enabled: !prev.probationary_notifications.email_enabled
                    }
                  }))}
                  className={`${
                    probationarySettings?.probationary_notifications?.email_enabled ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      probationarySettings?.probationary_notifications?.email_enabled ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">In-App Notifications</label>
                <button
                  type="button"
                  onClick={() => setProbationarySettings(prev => ({
                    ...prev,
                    probationary_notifications: {
                      ...prev.probationary_notifications,
                      in_app_enabled: !prev.probationary_notifications.in_app_enabled
                    }
                  }))}
                  className={`${
                    probationarySettings?.probationary_notifications?.in_app_enabled ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      probationarySettings?.probationary_notifications?.in_app_enabled ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Days Before Review
                </label>
                <div className="flex flex-wrap gap-2">
                  {(probationarySettings?.probationary_period_defaults?.notification_days_before || []).map((day, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                    >
                      {day} day{day !== 1 ? 's' : ''}
                      <button
                        type="button"
                        onClick={() => setProbationarySettings(prev => ({
                          ...prev,
                          probationary_period_defaults: {
                            ...prev.probationary_period_defaults,
                            notification_days_before: prev.probationary_period_defaults.notification_days_before.filter(d => d !== day)
                          }
                        }))}
                        className="ml-1 text-yellow-600 hover:text-yellow-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    type="number"
                    placeholder="Add days"
                    min="1"
                    max="365"
                    className="w-24 rounded-md border-gray-300 text-xs"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        const newDay = parseInt(e.target.value);
                        if (!(probationarySettings?.probationary_period_defaults?.notification_days_before || []).includes(newDay)) {
                          setProbationarySettings(prev => ({
                            ...prev,
                            probationary_period_defaults: {
                              ...prev.probationary_period_defaults,
                              notification_days_before: [...prev.probationary_period_defaults.notification_days_before, newDay].sort((a, b) => b - a)
                            }
                          }));
                        }
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Review Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Review Settings</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="required_reviews" className="block text-sm font-medium text-gray-700">
              Required Reviews
            </label>
            <input
              type="number"
              id="required_reviews"
              min="1"
              max="10"
              value={probationarySettings?.probationary_reviews?.required_reviews || 3}
              onChange={(e) => setProbationarySettings(prev => ({
                ...prev,
                probationary_reviews: {
                  ...prev.probationary_reviews,
                  required_reviews: parseInt(e.target.value)
                }
              }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="max_extensions" className="block text-sm font-medium text-gray-700">
              Maximum Extensions
            </label>
            <input
              type="number"
              id="max_extensions"
              min="0"
              max="10"
              value={probationarySettings?.probationary_reviews?.max_extensions || 2}
              onChange={(e) => setProbationarySettings(prev => ({
                ...prev,
                probationary_reviews: {
                  ...prev.probationary_reviews,
                  max_extensions: parseInt(e.target.value)
                }
              }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="extension_duration_months" className="block text-sm font-medium text-gray-700">
              Extension Duration (months)
            </label>
            <input
              type="number"
              id="extension_duration_months"
              min="1"
              max="12"
              value={probationarySettings?.probationary_reviews?.extension_duration_months || 3}
              onChange={(e) => setProbationarySettings(prev => ({
                ...prev,
                probationary_reviews: {
                  ...(prev?.probationary_reviews || {}),
                  extension_duration_months: parseInt(e.target.value)
                }
              }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div className="flex items-center">
            <div>
              <label className="text-sm font-medium text-gray-700">Allow Extensions</label>
              <p className="text-sm text-gray-500">Allow probationary period extensions</p>
            </div>
            <button
              type="button"
              onClick={() => setProbationarySettings(prev => ({
                ...prev,
                probationary_reviews: {
                  ...prev.probationary_reviews,
                  allow_extensions: !prev.probationary_reviews.allow_extensions
                }
              }))}
              className={`ml-4 ${
                probationarySettings?.probationary_reviews?.allow_extensions ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  probationarySettings?.probationary_reviews?.allow_extensions ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={async () => {
            try {
              setLoading(true);
              setMessage(''); // Clear any previous message
              
              console.log('Saving probationary settings:', probationarySettings);
              
              const response = await probationaryAPI.updateSettings(probationarySettings);
              console.log('Save response:', response);
              
              setMessage('Probationary settings saved successfully');
              
              // Clear message after 3 seconds
              setTimeout(() => {
                setMessage('');
              }, 3000);
              
              // Force refresh settings from server to ensure UI reflects saved changes
              setTimeout(async () => {
                console.log('Refreshing settings after save...');
                await loadProbationarySettings();
                console.log('Settings refreshed');
              }, 500);
              
            } catch (error) {
              console.error('Error saving probationary settings:', error);
              console.error('Error details:', error.response?.data);
              setMessage(`Error saving probationary settings: ${error.response?.data?.message || error.message}`);
            } finally {
              setLoading(false);
            }
          }}
          disabled={loading}
          className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileTab();
      case 'security':
        return renderSecurityTab();
      case 'kiosk':
        return renderKioskTab();
      case 'notifications':
        return renderNotificationsTab();
      case 'company':
        return renderCompanyTab();
      case 'workflows':
        return renderWorkflowsTab();
      case 'roles':
        return renderRoleManagementTab();
      case 'kiosk-management':
        return renderKioskManagementTab();
      case 'checkin-settings':
        return renderCheckinSettingsTab();
      case 'schedule-config':
        return renderScheduleConfigTab();
      case 'leave-settings':
        return renderLeaveSettingsTab();
      case 'document-types':
        return renderDocumentTypesTab();
      case 'probationary':
        return renderProbationaryTab();
      case 'system':
        return renderSystemTab();
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-600 mt-1">Manage your account and system settings</p>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-md ${
          message.includes('successfully') 
            ? 'bg-green-100 border border-green-400 text-green-700' 
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="card">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-start space-x-3 transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-50 text-primary-700 border-primary-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">{tab.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{tab.description}</div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <div className="card">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {tabs.find(tab => tab.id === activeTab)?.name}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {tabs.find(tab => tab.id === activeTab)?.description}
              </p>
            </div>
            
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;